import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ArrowLeft, ArrowRight, Building, Loader2, ShieldCheck, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Stepper, useStepper } from '@/components/ui/stepper';
import { Textarea } from '@/components/ui/textarea';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { formatDate } from '@/lib/utils/date';
import { useAuthStore } from '@/lib/auth/store';
import { useUpdateUser, useUserDetails } from '../api';
import { useAclSettings } from '../lookups/api';
import { asFormString, fromLegacyExpiry, toLegacyExpiry } from '../format';
import {
  updateSubSuperAdminSchema,
  type UpdateSubSuperAdminInput,
} from '../schema';
import { api } from '@/lib/api/client';
import { hexToHsl, hslToString } from '@/lib/theme/hsl-utils';

const STEP_FIELDS: Record<number, Array<keyof UpdateSubSuperAdminInput>> = {
  0: ['name', 'emailid', 'number'],
  1: ['company', 'address', 'pincode'],
  2: ['accountType', 'expiryDate', 'aclId'],
  3: [],
};

const STEPS = [
  { id: 'contact', label: 'Contact', description: 'Identification', icon: UserRound },
  { id: 'org', label: 'Organization', description: 'Company & address', icon: Building },
  { id: 'access', label: 'Access', description: 'Validity & ACL scope', icon: ShieldCheck },
  { id: 'review', label: 'Review', description: 'Confirm changes' },
] as const;

export function UpdateSubSuperAdminPage() {
  const navigate = useNavigate();
  const { userId: targetId } = useParams<{ userId: string }>();
  const targetUserId = Number(targetId);
  const parentId = Number(useAuthStore((s) => s.userId));

  const profile = useUserDetails(targetUserId);
  const aclQuery = useAclSettings(parentId, targetUserId);  
  const aclOptions = aclQuery.data ?? [];

  const update = useUpdateUser();
  const stepper = useStepper(STEPS.length);

  // UI Settings (themeColor + sidebarPosition) for this target user
  const [themeColor, setThemeColor] = useState('#2c5282');
  const [sidebarPosition, setSidebarPosition] = useState<'left' | 'right' | 'top'>('left');

  const form = useForm<UpdateSubSuperAdminInput>({
    resolver: zodResolver(updateSubSuperAdminSchema),
    mode: 'onTouched',
    defaultValues: {
      name: '',
      emailid: '',
      number: '',
      company: '',
      address: '',
      pincode: '',
      accountType: '0',
      expiryDate: '',
      aclId: '',
    },
  });

  // Fetch existing UI settings for this target user
  useEffect(() => {
    if (!targetUserId) return;
    api.get<{ json: string | null }>(`/api/obd/user/${targetUserId}/ui/settings`)
      .then((res) => {
        if (res?.json) {
          try {
            const parsed = JSON.parse(res.json);
            if (parsed?.themeColor) setThemeColor(parsed.themeColor);
            if (parsed?.sidebarPosition) setSidebarPosition(parsed.sidebarPosition);
          } catch { /* ignore */ }
        }
      })
      .catch(() => { /* silent */ });
  }, [targetUserId]);

  // Prefill once the profile lands.
  useEffect(() => {
    if (!profile.data) return;
    const p = profile.data;
    form.reset({
      name: asFormString(p.name),
      emailid: asFormString(p.emailid),
      number: asFormString(p.number),
      company: asFormString(p.company),
      address: asFormString(p.address),
      pincode: asFormString(p.pincode),
      accountType: String(p.accountType ?? 0) as '0' | '1' | '3',
      expiryDate: fromLegacyExpiry(p.expiryDate)?.toISOString() ?? '',
      aclId: asFormString(p.aclId),
    });
  }, [profile.data, form]);

  const handleNext = async () => {
    const fields = STEP_FIELDS[stepper.current];
    if (!fields || fields.length === 0) {
      stepper.next();
      return;
    }
    const ok = await form.trigger(fields);
    if (ok) stepper.next();
  };

  const onSubmit = (input: UpdateSubSuperAdminInput) => {
    update.mutate(
      {
        userId: targetUserId,
        parent: parentId,
        username: profile.data?.username ?? '',
        name: input.name.trim(),
        emailid: input.emailid.trim(),
        number: input.number.trim(),
        company: input.company.trim(),
        address: input.address.trim(),
        pincode: input.pincode.trim(),
        accountType: Number(input.accountType) as 0 | 1 | 3,
        userType: 'sub_sadmin',
        expiryDate: toLegacyExpiry(input.expiryDate),
        aclId: Number(input.aclId),
      },
      {
        onSuccess: async (res) => {
          try {
             const hsl = hexToHsl(themeColor);
            await api.post(`/api/obd/user/${targetUserId}/ui/settings`, {
              json: JSON.stringify({
                themeColor,
                sidebarPosition,
                custom: { primary: hsl ? hslToString(hsl) : null },
                sidebarPlacement: sidebarPosition,
              }),
            });
          } catch { /* silent */ }
          toast.success(res?.message ?? 'User updated');
          navigate('/sub-super-admin/user-list');
        },
        onError: (err) => {
          toast.error(readErrorMessage(err) ?? 'Could not update the user.');
        },
      },
    );
  };

  if (profile.isLoading) {
    return (
      <ScrollPage>
        <PageHeader title="Update Sub Super Admin" description="Loading…" />
        <FormSkeleton />
      </ScrollPage>
    );
  }

  return (
    <ScrollPage>
      <PageHeader
        title="Update Sub Super Admin"
        description={
          profile.data
            ? `Edit ${profile.data.username}'s details.`
            : 'Edit a sub super admin.'
        }
        actions={
          <Button asChild variant="outline">
            <Link to="/sub-super-admin/user-list">
              <ArrowLeft className="h-4 w-4" />
              Back to list
            </Link>
          </Button>
        }
      />

      <Card>
        <CardContent className="p-6">
          <Stepper
            steps={[...STEPS]}
            current={stepper.current}
            completed={stepper.completed}
            onStepClick={(i) => i <= stepper.current && stepper.goTo(i)}
          />
        </CardContent>
      </Card>

      <Form {...form}>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {stepper.current === 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Contact</CardTitle>
                <CardDescription>Identification and direct contact details.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Full name</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="emailid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} type="email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="number"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input {...field} type="tel" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Brand color */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Brand color</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={themeColor}
                      onChange={(e) => setThemeColor(e.target.value)}
                      className="h-9 w-14 cursor-pointer rounded border p-0.5"
                    />
                    <span className="text-sm text-muted-foreground">{themeColor}</span>
                  </div>
                </div>

                {/* Sidebar position */}
                <div className="space-y-2">
                  <label className="text-sm font-medium">Sidebar position</label>
                  <RadioGroup
                    value={sidebarPosition}
                    onValueChange={(v) => setSidebarPosition(v as 'left' | 'right' | 'top')}
                    className="flex gap-4"
                  >
                    <label className="flex cursor-pointer items-center gap-2 rounded-md border bg-card p-3 hover:border-ring">
                      <RadioGroupItem value="left" />
                      <span className="text-sm font-medium">Left</span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md border bg-card p-3 hover:border-ring">
                      <RadioGroupItem value="right" />
                      <span className="text-sm font-medium">Right</span>
                    </label>
                    <label className="flex cursor-pointer items-center gap-2 rounded-md border bg-card p-3 hover:border-ring">
                      <RadioGroupItem value="top" />
                      <span className="text-sm font-medium">Top</span>
                    </label>
                  </RadioGroup>
                </div>

              </CardContent>
            </Card>
          )}

          {stepper.current === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Organization</CardTitle>
                <CardDescription>Company and mailing address.</CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Company</FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="address"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Address</FormLabel>
                      <FormControl>
                        <Textarea {...field} rows={3} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="pincode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Pincode</FormLabel>
                      <FormControl>
                        <Input {...field} inputMode="numeric" maxLength={10} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>Access</CardTitle>
                <CardDescription>
                  Billing mode, validity, and the ACL set that scopes which modules this admin can
                  manage.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-4 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="accountType"
                  render={({ field }) => (
                    <FormItem className="md:col-span-2">
                      <FormLabel>Account type</FormLabel>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={field.onChange}
                          className="grid grid-cols-2 gap-3"
                        >
                          {(
                            [        
                              { value: '0', label: 'Promotional' },
                              { value: '1', label: 'Transactional' },
                               { value: '3', label: 'Both' }, 
                            ] as const
                          ).map((opt) => (
                            <label
                              key={opt.value}
                              className="flex cursor-pointer items-center gap-3 rounded-md border bg-card p-3 hover:border-ring"
                            >
                              <RadioGroupItem value={opt.value} />
                              <span className="text-sm font-medium">{opt.label}</span>
                            </label>
                          ))}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="expiryDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Validity expires on</FormLabel>
                      <FormControl>
                        <DatePicker
                          value={field.value ? new Date(field.value) : null}
                          onChange={(d) => field.onChange(d ? d.toISOString() : '')}
                          fromDate={new Date()}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="aclId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>ACL setting</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={field.onChange}
                        disabled={aclQuery.isLoading}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue
                              placeholder={aclQuery.isLoading ? 'Loading…' : 'Pick an ACL set'}
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {aclOptions.map((opt) => (
                            <SelectItem key={opt.aclId} value={String(opt.aclId)}>
                              {opt.aclName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>
          )}

          {stepper.current === 3 && (
            <ReviewStep
              form={form}
              aclLookup={Object.fromEntries(aclOptions.map((o) => [String(o.aclId), o.aclName]))}
              themeColor={themeColor}
              sidebarPosition={sidebarPosition}
            />
          )}

          <div className="sticky bottom-0 -mx-4 mt-2 flex items-center justify-between gap-3 border-t bg-background/95 px-4 py-3 backdrop-blur lg:-mx-8 lg:px-8">
            <Button
              type="button"
              variant="ghost"
              onClick={stepper.prev}
              disabled={stepper.isFirst || update.isPending}
            >
              <ArrowLeft className="h-4 w-4" />
              Previous
            </Button>

            <div className="text-xs text-muted-foreground">
              Step {stepper.current + 1} of {STEPS.length}
            </div>

            <div className="flex items-center gap-2">
              <Button asChild variant="ghost">
                <Link to="/sub-super-admin/user-list">Cancel</Link>
              </Button>
              <Button
                type="button"
                disabled={update.isPending}
                onClick={async () => {
                  if (!stepper.isLast) {
                    await handleNext();
                  } else {
                    form.handleSubmit(onSubmit)();
                  }
                }}
              >
                {update.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {stepper.isLast ? 'Save changes' : <>Next <ArrowRight className="h-4 w-4" /></>}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </ScrollPage>
  );
}

function ReviewStep({
  form,
  aclLookup,
  themeColor,
  sidebarPosition,
}: {
  form: ReturnType<typeof useForm<UpdateSubSuperAdminInput>>;
  aclLookup: Record<string, string>;
  themeColor: string;
  sidebarPosition: string;
}) {
  const v = form.getValues();
  const expiry = v.expiryDate ? formatDate(new Date(v.expiryDate)) : '—';
  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
        <CardDescription>
          Confirm the changes. Click any earlier step header to make edits.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <ReviewSection title="Contact">
          <Row label="Full name" value={v.name} />
          <Row label="Email" value={v.emailid} />
          <Row label="Phone" value={v.number} />
        </ReviewSection>
        <ReviewSection title="Organization">
          <Row label="Company" value={v.company} />
          <Row label="Address" value={v.address} multiline />
          <Row label="Pincode" value={v.pincode} />
        </ReviewSection>
        <ReviewSection title="Access">
          <Row label="Account type" value={v.accountType === '3' ? 'Both' : v.accountType === '1' ? 'Transactional' : 'Promotional'} />
          <Row label="Validity expires" value={expiry} />
          <Row label="ACL setting" value={aclLookup[v.aclId] ?? `#${v.aclId}`} />
          <Row label="Brand color" value={themeColor} />
          <Row label="Sidebar position" value={sidebarPosition} />
        </ReviewSection>
      </CardContent>
    </Card>
  );
}

function ReviewSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <dl className="grid grid-cols-1 gap-x-6 gap-y-2 md:grid-cols-2">{children}</dl>
    </div>
  );
}

function Row({ label, value, multiline }: { label: string; value?: string; multiline?: boolean }) {
  return (
    <div className={multiline ? 'md:col-span-2' : undefined}>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className={`text-sm font-medium text-foreground ${multiline ? 'whitespace-pre-line' : ''}`}>
        {value || '—'}
      </dd>
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2].map((s) => (
        <Card key={s}>
          <CardContent className="space-y-3 p-6">
            <Skeleton className="h-5 w-1/4" />
            <div className="grid gap-4 md:grid-cols-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-9 w-full" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
